import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FacturacionMasivaService } from '../../../services/facturacion-masiva/facturacion-masiva.service';
import swal2 from 'sweetalert2';

// Página de detalle de un lote de facturación masiva. 23/07/2026
@Component({
  selector: 'app-detalle-lote',
  templateUrl: './detalle-lote.component.html',
  styleUrls: ['./detalle-lote.component.css']
})
export class DetalleLoteComponent implements OnInit {

  batch: any = null;
  loading = false;
  batchId: number;

  // Paginación y filtro de filas
  searchTerm = '';
  currentPage = 1;
  itemsPerPage = 15;
  filaSeleccionada: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private facturacionService: FacturacionMasivaService
  ) {}

  ngOnInit() {
    this.batchId = +this.route.snapshot.params['id'];
    this.loadBatch();
  }

  // Carga el detalle completo del lote desde el backend. 23/07/2026
  loadBatch() {
    this.loading = true;
    this.facturacionService.getBatch(this.batchId).subscribe(
      (resp: any) => {
        this.batch = resp && resp.data ? resp.data : resp;
        this.loading = false;
      },
      (err) => {
        console.error('Error al cargar lote', err);
        this.batch = null;
        this.loading = false;
        if (err.status === 404) {
          swal2.fire('No encontrado', 'El lote solicitado no existe.', 'warning');
        }
      }
    );
  }

  // Regresa a la página principal de facturación masiva. 23/07/2026
  regresar() {
    this.router.navigate(['/facturacionmasiva']);
  }

  // Obtiene solo las filas con error o duplicadas para exportar. 23/07/2026
  getErrorRows(): any[] {
    if (!this.batch || !this.batch.rows) {
      return [];
    }
    return this.batch.rows.filter(r => r.row_status === 'ERROR' || r.row_status === 'DUPLICADA');
  }

  // Exporta las filas con error a un archivo CSV descargable. 23/07/2026
  exportarErrores() {
    const errorRows = this.getErrorRows();
    if (errorRows.length === 0) {
      swal2.fire('Sin errores', 'No hay filas con error para exportar.', 'info');
      return;
    }

    const headers = ['Fila', 'Estatus', 'Tipo Documento', 'Folio Interno', 'RFC Emisor', 'RFC Receptor', 'Total', 'Error'];
    const csvContent = [
      headers.join(','),
      ...errorRows.map(r => [
        r.row_number,
        r.row_status,
        r.tipo_documento || '',
        r.folio_interno || '',
        r.emitter_rfc || '',
        r.receiver_rfc || '',
        r.total || '',
        `"${(r.error_message || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `errores_lote_${this.batchId}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  // Retorna clase CSS según estatus del lote. 23/07/2026
  getStatusClass(status: string): string {
    switch (status) {
      case 'COMPLETADO': return 'badge-success';
      case 'COMPLETADO_CON_ERRORES': return 'badge-warning';
      case 'ERROR': return 'badge-danger';
      case 'PROCESANDO': return 'badge-info';
      case 'PENDIENTE': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  }

  // Retorna texto legible del estatus del lote. 23/07/2026
  getStatusLabel(status: string): string {
    switch (status) {
      case 'COMPLETADO': return 'Completado';
      case 'COMPLETADO_CON_ERRORES': return 'Con errores';
      case 'ERROR': return 'Error';
      case 'PROCESANDO': return 'Procesando';
      case 'PENDIENTE': return 'Pendiente';
      default: return status;
    }
  }

  // Retorna clase CSS según estatus de fila individual. 23/07/2026
  getRowStatusClass(status: string): string {
    switch (status) {
      case 'EXITOSA': return 'badge-success';
      case 'ERROR': return 'badge-danger';
      case 'DUPLICADA': return 'badge-warning';
      case 'PENDIENTE': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  }

  // --- Paginación y filtro de filas. 23/07/2026 ---

  get filteredRows(): any[] {
    if (!this.batch || !this.batch.rows) {
      return [];
    }
    if (!this.searchTerm) {
      return this.batch.rows;
    }
    const term = this.searchTerm.toLowerCase();
    return this.batch.rows.filter(row =>
      (row.row_status && row.row_status.toLowerCase().includes(term)) ||
      (row.folio_interno && row.folio_interno.toLowerCase().includes(term)) ||
      (row.emitter_rfc && row.emitter_rfc.toLowerCase().includes(term)) ||
      (row.receiver_rfc && row.receiver_rfc.toLowerCase().includes(term)) ||
      (row.error_message && row.error_message.toLowerCase().includes(term)) ||
      (row.uuid && row.uuid.toLowerCase().includes(term))
    );
  }

  get dataToShow(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredRows.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredRows.length / this.itemsPerPage);
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  resetPage() {
    this.currentPage = 1;
    this.filaSeleccionada = null;
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  seleccionarFila(index: number) {
    this.filaSeleccionada = index + (this.currentPage - 1) * this.itemsPerPage;
  }

  // Muestra el mensaje de error completo en un modal. 23/07/2026
  verErrorCompleto(row: any, event: Event) {
    event.stopPropagation();
    if (!row.error_message) {
      return;
    }
    swal2.fire({
      title: `Error en fila ${row.row_number}`,
      html: `<div style="text-align: left; word-break: break-word; white-space: pre-wrap; font-size: 14px; color: #333; max-height: 60vh; overflow-y: auto;">${this.escapeHtml(row.error_message)}</div>`,
      icon: 'error',
      confirmButtonText: 'Cerrar',
      width: '600px'
    });
  }

  // Escapa caracteres HTML para prevenir inyección. 23/07/2026
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

}
